import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    RefreshCw, Radar, Cpu, GitBranch, Database, ChevronDown, ChevronRight, PlayCircle, Clock, Flame,
    ThermometerSun, ThermometerSnowflake, Activity, X, StopCircle, Beaker, Inbox, BookOpen, Calculator,
} from "lucide-react";
import { toast } from "sonner";
import { ReevaluationPanel } from "@/components/ripeness/ReevaluationPanel";
import { AreaHealthPanel } from "@/components/ripeness/AreaHealthPanel";
import { SourcesPanel } from "@/components/ripeness/SourcesPanel";
import { ExternalSourcesPanel } from "@/components/ripeness/ExternalSourcesPanel";
import { ValueHealthCard } from "@/components/ripeness/value/ValueHealthCard";
import { ValueWarningsBanner } from "@/components/ripeness/value/ValueWarningsBanner";
import { Link } from "react-router-dom";
import { RipenessFilterPanel } from "@/components/ripeness/RipenessFilterPanel";
import { RipenessTable } from "@/components/ripeness/RipenessTable";
import { useRipenessFilters } from "@/components/ripeness/useRipenessFilters";
import { fmtDate, fmtMoney } from "@/components/ripeness/radarMeta";
import { ReplicaBanner } from "@/components/radar_sync/ReplicaBanner";

function StatTile({ label, value, tone, Icon }) {
    return (
        <div
            className={`rounded-lg border ${tone || "border-slate-700/60 bg-slate-800/40"} p-4`}
            data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="flex items-center justify-between text-xs uppercase tracking-wider text-slate-400">
                <span>{label}</span>
                {Icon ? <Icon className="w-4 h-4" /> : null}
            </div>
            <div className="mt-2 text-2xl font-semibold text-slate-100">{value}</div>
        </div>
    );
}

function BackfillModal({ open, onClose, onSubmit, submitting }) {
    const [form, setForm] = useState({
        request_id: `radar-${Date.now()}`,
        mode: "unevaluated",
        max_records: 25,
        signal_extractor: "rules-1.6",
        llm_match_escalate: false,
        webhook_url: "",
        reason: "QA dashboard trigger",
        dry_run: false,
    });
    useEffect(() => {
        if (open) {
            setForm((f) => ({ ...f, request_id: `radar-${Date.now()}` }));
        }
    }, [open]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4" data-testid="backfill-modal">
            <div className="w-full max-w-lg rounded-lg border border-slate-700/60 bg-slate-900/95 p-6 shadow-2xl">
                <div className="flex items-center justify-between border-b border-slate-700/60 pb-3">
                    <h3 className="text-lg font-semibold text-slate-100 inline-flex items-center gap-2">
                        <PlayCircle className="w-5 h-5 text-emerald-400" /> Run backfill
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-200" data-testid="backfill-modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="grid grid-cols-1 gap-3 mt-4">
                    <label className="text-xs text-slate-400 flex flex-col gap-1">
                        Reason
                        <input
                            value={form.reason}
                            onChange={(e) => setForm({ ...form, reason: e.target.value })}
                            className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1.5 text-sm text-slate-100"
                            data-testid="backfill-reason-input"
                        />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="text-xs text-slate-400 flex flex-col gap-1">
                            Max records
                            <input
                                type="number"
                                value={form.max_records}
                                min={1}
                                onChange={(e) => setForm({ ...form, max_records: Number(e.target.value) })}
                                className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1.5 text-sm text-slate-100"
                                data-testid="backfill-max-records-input"
                            />
                        </label>
                        <label className="text-xs text-slate-400 flex flex-col gap-1">
                            Mode
                            <select
                                value={form.mode}
                                onChange={(e) => setForm({ ...form, mode: e.target.value })}
                                className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1.5 text-sm text-slate-100"
                                data-testid="backfill-mode-select"
                            >
                                <option value="unevaluated">unevaluated</option>
                                <option value="stale">stale</option>
                            </select>
                        </label>
                    </div>
                    <label className="text-xs text-slate-400 flex flex-col gap-1">
                        Signal extractor
                        <select
                            value={form.signal_extractor}
                            onChange={(e) => setForm({ ...form, signal_extractor: e.target.value })}
                            className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1.5 text-sm text-slate-100"
                            data-testid="backfill-extractor-select"
                        >
                            <option value="rules-1.6">rules-1.6 (deterministic, free)</option>
                            <option value="llm-extract-1.2">llm-extract-1.2 (Claude Sonnet 5.5)</option>
                        </select>
                    </label>
                    <label className="text-xs text-slate-400 flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={form.llm_match_escalate}
                            onChange={(e) => setForm({ ...form, llm_match_escalate: e.target.checked })}
                            data-testid="backfill-escalate-check"
                        />
                        LLM ambiguous-match escalation
                    </label>
                    <label className="text-xs text-slate-400 flex flex-col gap-1">
                        Webhook URL (optional)
                        <input
                            value={form.webhook_url}
                            placeholder="https://your-endpoint/hooks"
                            onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                            className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1.5 text-sm text-slate-100"
                            data-testid="backfill-webhook-input"
                        />
                    </label>
                    <label className="text-xs text-slate-400 flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={form.dry_run}
                            onChange={(e) => setForm({ ...form, dry_run: e.target.checked })}
                            data-testid="backfill-dryrun-check"
                        />
                        Dry-run (no writes)
                    </label>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSubmit({
                            ...form,
                            webhook_url: form.webhook_url?.trim() || undefined,
                        })}
                        disabled={submitting || !form.reason || form.reason.length < 5}
                        className="rounded bg-emerald-500/80 hover:bg-emerald-500 disabled:opacity-40 px-3 py-1.5 text-sm text-white inline-flex items-center gap-1.5"
                        data-testid="backfill-submit-btn"
                    >
                        {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                        Start backfill
                    </button>
                </div>
            </div>
        </div>
    );
}

const STATUS_TONE = (status) => status === "done"
    ? "border-emerald-400/40 text-emerald-300"
    : status === "failed"
        ? "border-rose-400/40 text-rose-300"
        : ["queued", "processing", "paused"].includes(status)
            ? "border-sky-400/40 text-sky-300"
            : "border-slate-500/30 text-slate-400";

function BatchRow({ batch }) {
    const isActive = ["queued", "processing"].includes(batch.status);
    const c = batch.counts || {};
    const tc = batch.tier_counts || {};
    return (
        <div className="border border-slate-800 rounded p-2 mb-1.5 text-xs" data-testid={`batch-row-${batch.batch_id}`}>
            <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 uppercase tracking-wider ${STATUS_TONE(batch.status)}`}
                    data-testid={`batch-status-${batch.batch_id}`}>
                    {isActive ? <RefreshCw className="w-3 h-3 inline mr-1 animate-spin" /> : null}{batch.status}
                </span>
                <span className="text-slate-400 font-mono truncate">{batch.batch_id}</span>
                <span className="ml-auto text-slate-500 whitespace-nowrap">{fmtDate(batch.finished_at || batch.submitted_at)}</span>
            </div>
            <div className="text-slate-500 mt-1 flex flex-wrap gap-x-1.5">
                <span className="text-slate-300">{c.received ?? 0} received</span>
                · <span className="text-emerald-300">{c.accepted ?? 0} accepted</span>
                · <span className="text-amber-300">{c.duplicates ?? 0} dup</span>
                · {batch.progress?.tracks_evaluated || 0} tracks
                {batch.status === "done" || batch.status === "processing" ? (
                    <> · <span className="text-rose-300">{tc.hot || 0}H</span>
                        <span className="text-orange-300">{tc.warm || 0}W</span>
                        <span className="text-amber-300">{tc.watch || 0}w</span>
                        <span className="text-slate-400">{tc.cold || 0}C</span></>
                ) : null}
            </div>
            {batch.request_id ? <div className="text-slate-600 mt-0.5 truncate">req: {batch.request_id}</div> : null}
            {batch.client_id ? <div className="text-slate-600 truncate">tenant: {batch.client_id}</div> : null}
        </div>
    );
}

function JobRow({ job, onCancel }) {
    const isActive = ["queued", "processing", "paused"].includes(job.status);
    const tone = job.status === "done"
        ? "border-emerald-400/40 text-emerald-300"
        : job.status === "failed"
            ? "border-rose-400/40 text-rose-300"
            : isActive
                ? "border-sky-400/40 text-sky-300"
                : "border-slate-500/30 text-slate-400";
    return (
        <div className="border border-slate-800 rounded p-2 mb-1.5 text-xs" data-testid={`job-row-${job.job_id}`}>
            <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 uppercase tracking-wider ${tone}`}>{job.status}</span>
                {job.kind === "reevaluation" ? (
                    <span className="rounded border border-teal-400/40 text-teal-300 px-1.5 py-0.5 text-[10px] uppercase tracking-wider" data-testid={`job-kind-reeval-${job.job_id}`}>re-eval</span>
                ) : null}
                <span className="text-slate-400 font-mono truncate">{job.job_id}</span>
                <span className="text-slate-500 hidden md:inline truncate">{job.input?.reason || ""}</span>
                {isActive ? (
                    <button
                        onClick={() => onCancel(job.job_id)}
                        className="ml-auto text-rose-300 hover:text-rose-200 inline-flex items-center gap-1"
                        title="Cancel"
                        data-testid={`job-cancel-${job.job_id}`}
                    >
                        <StopCircle className="w-3.5 h-3.5" />
                    </button>
                ) : (
                    <span className="ml-auto text-slate-500">{fmtDate(job.finished_at || job.submitted_at)}</span>
                )}
            </div>
            <div className="text-slate-500 mt-1">
                {job.progress?.programs_done || 0} programs · {job.progress?.tracks_evaluated || 0} tracks
                {job.tier_counts ? (
                    <> · <span className="text-rose-300">{job.tier_counts.hot || 0} hot</span>
                        · <span className="text-orange-300">{job.tier_counts.warm || 0} warm</span>
                        · <span className="text-amber-300">{job.tier_counts.watch || 0} watch</span>
                        · <span className="text-slate-400">{job.tier_counts.cold || 0} cold</span></>
                ) : null}
                {job.progress?.llm_total_cost_usd ? <> · ${job.progress.llm_total_cost_usd.toFixed(3)} LLM</> : null}
            </div>
        </div>
    );
}

export default function QaRipenessRadarPage() {
    const rf = useRipenessFilters(100);
    const { facets, tracks, totalTracks, valueTotal, loading, tierTotals, refresh } = rf;
    const [jobs, setJobs] = useState([]);
    const [showJobs, setShowJobs] = useState(true);
    const [batches, setBatches] = useState([]);
    const [showBatches, setShowBatches] = useState(true);
    const [openBackfill, setOpenBackfill] = useState(false);
    const [submittingBackfill, setSubmittingBackfill] = useState(false);

    const loadJobs = useCallback(async () => {
        try {
            const r = await api.get("/v1/ripeness/backfill-jobs?limit=10");
            setJobs(r.data.items || []);
        } catch (e) {
            console.warn("Radar: jobs poll failed", formatApiErrorDetail(e));
        }
    }, []);

    const loadBatches = useCallback(async () => {
        try {
            const r = await api.get("/v1/ripeness/opportunities/batches?limit=10");
            setBatches(r.data.items || []);
        } catch (e) {
            console.warn("Radar: batches poll failed", formatApiErrorDetail(e));
        }
    }, []);

    // Poll jobs + batches every 5s while any is active, else every 30s.
    const anyActive = useMemo(
        () => [...jobs, ...batches].some((x) => ["queued", "processing", "paused"].includes(x.status)),
        [jobs, batches],
    );
    useEffect(() => {
        loadJobs();
        loadBatches();
        const id = setInterval(() => { loadJobs(); loadBatches(); }, anyActive ? 5000 : 30000);
        return () => clearInterval(id);
    }, [loadJobs, loadBatches, anyActive]);

    // When a batch lands, refresh the radar.
    const doneBatchKey = useMemo(
        () => batches.filter((b) => b.status === "done").map((b) => b.batch_id).join(","),
        [batches],
    );
    useEffect(() => {
        if (doneBatchKey) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doneBatchKey]);

    const excludeTrack = useCallback(async (track) => {
        const reason = window.prompt(`Exclude ${track.compound?.name || track.track_id} × ${track.indication?.label || track.indication_key}?\nReason (audited):`, "");
        if (reason === null) return false;
        if (reason.trim().length < 3) { toast.error("Reason must be at least 3 characters"); return false; }
        try {
            await api.post("/v1/ripeness/exclusions", { scope: "track", target_id: track.track_id, reason: reason.trim() });
            toast.success("Track excluded");
            await refresh();
            return true;
        } catch (e) {
            toast.error(`Exclude failed: ${formatApiErrorDetail(e)}`);
            return false;
        }
    }, [refresh]);

    const restoreTrack = useCallback(async (excluded) => {
        try {
            await api.delete(`/v1/ripeness/exclusions/${excluded.exclusion_id}`, { params: { reason: "Restored from QA dashboard" } });
            toast.success("Exclusion removed");
            await refresh();
            return true;
        } catch (e) {
            toast.error(`Restore failed: ${formatApiErrorDetail(e)}`);
            return false;
        }
    }, [refresh]);

    const submitBackfill = useCallback(async (form) => {
        setSubmittingBackfill(true);
        try {
            const r = await api.post("/v1/ripeness/backfill-jobs", form, {
                headers: { "Idempotency-Key": form.request_id },
            });
            toast.success(`Backfill queued: ${r.data.job_id}`);
            setJobs((prev) => [{ ...r.data, input: form }, ...prev].slice(0, 20));
            setOpenBackfill(false);
            // Poll the specific job every 4s until terminal
            const poll = async () => {
                try {
                    const g = await api.get(`/v1/ripeness/backfill-jobs/${r.data.job_id}`);
                    setJobs((prev) => {
                        const others = prev.filter((j) => j.job_id !== g.data.job_id);
                        return [g.data, ...others].slice(0, 20);
                    });
                    if (["done", "failed", "cancelled"].includes(g.data.status)) {
                        await refresh();
                        return;
                    }
                    setTimeout(poll, 4000);
                } catch (e) {
                    console.warn("Radar: backfill job poll failed", formatApiErrorDetail(e));
                }
            };
            setTimeout(poll, 2000);
        } catch (e) {
            toast.error(`Backfill submit failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setSubmittingBackfill(false);
        }
    }, [refresh]);

    const cancelJob = useCallback(async (jobId) => {
        try {
            await api.post(`/v1/ripeness/backfill-jobs/${jobId}/cancel`, { reason: "Cancelled from QA dashboard" });
            toast.success("Cancel requested");
            const g = await api.get(`/v1/ripeness/backfill-jobs/${jobId}`);
            setJobs((prev) => {
                const others = prev.filter((j) => j.job_id !== jobId);
                return [g.data, ...others];
            });
        } catch (e) {
            toast.error(`Cancel failed: ${formatApiErrorDetail(e)}`);
        }
    }, []);

    return (
        <div className="p-6 max-w-[1600px] mx-auto" data-testid="qa-ripeness-radar-page">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                    <h1 className="text-2xl font-semibold text-slate-100 inline-flex items-center gap-2">
                        <Radar className="w-6 h-6 text-emerald-400" /> Trial Ripeness Radar
                    </h1>
                    <p className="text-sm text-slate-400 mt-1 max-w-2xl">
                        Real-time view of the SC-WN-M07 Opportunity Ripeness Backfill pipeline. Each track scores
                        one compound × indication combination against ClinicalTrials.gov v2 + news signals.
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1 border border-slate-700 rounded px-2 py-0.5">
                            <Cpu className="w-3 h-3" /> Model ripeness-1.6.2
                        </span>
                        <span className="inline-flex items-center gap-1 border border-slate-700 rounded px-2 py-0.5">
                            <GitBranch className="w-3 h-3" /> Skill ripeness-backfill-1.0
                        </span>
                        <span className="inline-flex items-center gap-1 border border-slate-700 rounded px-2 py-0.5">
                            <Database className="w-3 h-3" /> Source news_api_ai
                        </span>
                        <span className="inline-flex items-center gap-1 border border-slate-700 rounded px-2 py-0.5">
                            <Beaker className="w-3 h-3" /> CT.gov v2
                        </span>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <a
                        href="/downloads/ripeness-radar-api.md"
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded border border-slate-700 bg-slate-800/40 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
                        data-testid="radar-api-guide-link"
                    >
                        <BookOpen className="w-4 h-4" /> API guide
                    </a>
                    <Link
                        to="/qa/value-assumptions"
                        className="inline-flex items-center gap-1.5 rounded border border-emerald-400/40 bg-emerald-400/10 px-3 py-1.5 text-sm text-emerald-200 hover:bg-emerald-400/20"
                        title="Lead-value assumptions — global defaults and per-client budgets, locations, weights, calibration"
                        data-testid="radar-value-assumptions-btn"
                    >
                        <Calculator className="w-4 h-4" /> Value assumptions
                    </Link>
                    <button
                        onClick={() => refresh()}
                        className="inline-flex items-center gap-1.5 rounded border border-slate-700 bg-slate-800/40 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
                        data-testid="radar-refresh-btn"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                    <button
                        onClick={() => setOpenBackfill(true)}
                        className="inline-flex items-center gap-1.5 rounded bg-emerald-500/80 hover:bg-emerald-500 px-3 py-1.5 text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="radar-new-backfill-btn"
                    >
                        <PlayCircle className="w-4 h-4" /> Run backfill
                    </button>
                </div>
            </div>
            <ReplicaBanner />
            <ValueWarningsBanner />

            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
                <StatTile label="Total tracks" value={facets?.total_tracks ?? "—"} Icon={Radar} />
                <StatTile label="Hot" value={tierTotals.hot} tone="border-rose-400/30 bg-rose-400/5" Icon={Flame} />
                <StatTile label="Warm" value={tierTotals.warm} tone="border-orange-400/30 bg-orange-400/5" Icon={ThermometerSun} />
                <StatTile label="Watch" value={tierTotals.watch} tone="border-amber-400/30 bg-amber-400/5" Icon={Activity} />
                <StatTile label="Cold" value={tierTotals.cold} tone="border-slate-400/20 bg-slate-400/5" Icon={ThermometerSnowflake} />
                <StatTile label={valueTotal?.has_weights ? "Value to us" : "Expected value"} value={valueTotal ? fmtMoney(valueTotal.has_weights ? valueTotal.weighted_mid : valueTotal.expected_mid, valueTotal.currency) : "—"} tone="border-emerald-400/30 bg-emerald-400/5" Icon={Calculator} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <aside className="lg:col-span-1 space-y-4">
                    <RipenessFilterPanel rf={rf} />

                    <ReevaluationPanel onRunStarted={() => loadJobs()} />

                    <AreaHealthPanel onChanged={() => rf.refresh()} />

                    <ValueHealthCard />

                    <SourcesPanel onChanged={() => rf.refresh()} />

                    <ExternalSourcesPanel onChanged={() => rf.refresh()} />

                    <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4">
                        <button
                            onClick={() => setShowJobs(!showJobs)}
                            className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 mb-2"
                        >
                            <span className="inline-flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5" /> Recent jobs
                            </span>
                            {showJobs ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        </button>
                        {showJobs ? (
                            jobs.length === 0 ? (
                                <div className="text-xs text-slate-500 italic">Start a backfill to see jobs here.</div>
                            ) : (
                                <div>
                                    {jobs.map((j) => (
                                        <JobRow key={j.job_id} job={j} onCancel={cancelJob} />
                                    ))}
                                </div>
                            )
                        ) : null}
                    </div>

                    <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="recent-batches-panel">
                        <button
                            onClick={() => setShowBatches(!showBatches)}
                            className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 mb-2"
                            data-testid="recent-batches-toggle"
                        >
                            <span className="inline-flex items-center gap-1.5">
                                <Inbox className="w-3.5 h-3.5" /> Recent batches
                                <span className="text-slate-600 normal-case tracking-normal">({batches.length})</span>
                            </span>
                            {showBatches ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        </button>
                        {showBatches ? (
                            batches.length === 0 ? (
                                <div className="text-xs text-slate-500 italic" data-testid="recent-batches-empty">
                                    No batches yet. Push opportunities to
                                    <code className="ml-1 text-slate-400">POST /api/v1/ripeness/opportunities/batch</code>.
                                </div>
                            ) : (
                                <div>
                                    {batches.map((b) => (
                                        <BatchRow key={b.batch_id} batch={b} />
                                    ))}
                                </div>
                            )
                        ) : null}
                    </div>
                </aside>

                <section className="lg:col-span-3">
                    <RipenessTable tracks={tracks} totalTracks={totalTracks} valueTotal={valueTotal} loading={loading} onExclude={excludeTrack} onRestore={restoreTrack} />
                </section>
            </div>


            <BackfillModal
                open={openBackfill}
                onClose={() => setOpenBackfill(false)}
                onSubmit={submitBackfill}
                submitting={submittingBackfill}
            />
        </div>
    );
}
